import { useEffect, useRef, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const T = "#2dd4bf";

function sid() {
  let s = localStorage.getItem("wbp_chat_sid");
  if (!s) {
    s = Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem("wbp_chat_sid", s);
  }
  return s;
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([
    { role: "assistant", text: "Hi! I'm the Watermark Batch Pro assistant. Ask me anything — installing, the free trial, license keys, or report a problem." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [escalate, setEscalate] = useState(false);
  const [form, setForm] = useState({ name: "", email: "" });
  const [escState, setEscState] = useState("idle");
  const bottom = useRef(null);

  useEffect(() => {
    bottom.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs, open, escalate]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", text }, { role: "assistant", text: "" }]);
    try {
      const res = await fetch(`${API}/api/support/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sid(), message: text }),
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const d = JSON.parse(p.slice(6));
          if (d.delta) {
            setMsgs((m) => {
              const c = [...m];
              c[c.length - 1] = { role: "assistant", text: c[c.length - 1].text + d.delta };
              return c;
            });
          }
        }
      }
    } catch (e) {
      setMsgs((m) => {
        const c = [...m];
        c[c.length - 1] = { role: "assistant", text: "Sorry — I couldn't connect. Please try again or use the contact form in the Support section." };
        return c;
      });
    }
    setBusy(false);
  };

  const sendTranscript = async () => {
    if (!form.name || !form.email) { setEscState("missing"); return; }
    setEscState("sending");
    try {
      await axios.post(`${API}/api/support/chat/transcript`,
        { session_id: sid(), ...form });
      setEscState("sent");
    } catch (e) { setEscState("error"); }
  };

  return (
    <>
      <button onClick={() => setOpen(!open)} data-testid="chat-widget-btn"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full font-bold text-2xl text-black shadow-2xl transition-transform hover:scale-110"
        style={{ background: T }}>
        {open ? "×" : "?"}
      </button>
      {open && (
        <div className="fixed bottom-24 right-6 z-50 w-[360px] max-w-[calc(100vw-3rem)] rounded-2xl border border-neutral-700 shadow-2xl flex flex-col overflow-hidden"
             style={{ background: "#0e1313", height: "480px" }}
             data-testid="chat-widget-panel">
          <div className="px-4 py-3 border-b border-neutral-800 flex items-center justify-between">
            <div>
              <div className="font-bold text-sm text-white">Support Assistant</div>
              <div className="text-xs" style={{ color: T }}>● Online — answers instantly</div>
            </div>
            <button onClick={() => setEscalate(!escalate)} data-testid="chat-escalate-btn"
              className="text-xs px-3 py-1.5 rounded-lg border border-neutral-700 text-neutral-300 hover:bg-neutral-900">
              Email this chat to support
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-xl px-3 py-2 text-sm whitespace-pre-wrap ${m.role === "user" ? "text-black" : "text-neutral-200 bg-neutral-900"}`}
                     style={m.role === "user" ? { background: T } : {}}
                     data-testid={`chat-msg-${m.role}-${i}`}>
                  {m.text || "…"}
                </div>
              </div>
            ))}
            {escalate && (
              <div className="rounded-xl border border-neutral-700 p-3 space-y-2"
                   data-testid="chat-escalate-form">
                {escState === "sent" ? (
                  <div className="text-sm" style={{ color: T }}>
                    ✓ Sent! The developer will reply to {form.email} within 24 hours.</div>
                ) : (
                  <>
                    <div className="text-xs text-neutral-400">
                      Send this conversation to the developer — you'll get a reply by email.</div>
                    <input className="w-full p-2 rounded bg-black border border-neutral-700 text-sm text-white"
                      placeholder="Your name" value={form.name} data-testid="chat-escalate-name"
                      onChange={(e) => setForm({ ...form, name: e.target.value })} />
                    <input className="w-full p-2 rounded bg-black border border-neutral-700 text-sm text-white"
                      placeholder="Your email" value={form.email} data-testid="chat-escalate-email"
                      onChange={(e) => setForm({ ...form, email: e.target.value })} />
                    <button onClick={sendTranscript} disabled={escState === "sending"}
                      data-testid="chat-escalate-send"
                      className="w-full py-2 rounded-lg font-semibold text-black text-sm disabled:opacity-60"
                      style={{ background: T }}>
                      {escState === "sending" ? "Sending…" : "Send to Support"}
                    </button>
                    {escState === "missing" && <div className="text-orange-400 text-xs">Name and email required.</div>}
                    {escState === "error" && <div className="text-red-400 text-xs">Could not send — chat a message first, then retry.</div>}
                  </>
                )}
              </div>
            )}
            <div ref={bottom} />
          </div>
          <div className="p-3 border-t border-neutral-800 flex gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Type your question…" data-testid="chat-input"
              className="flex-1 p-2.5 rounded-lg bg-black border border-neutral-700 text-sm text-white" />
            <button onClick={send} disabled={busy} data-testid="chat-send-btn"
              className="px-4 rounded-lg font-semibold text-black text-sm disabled:opacity-60"
              style={{ background: T }}>➤</button>
          </div>
        </div>
      )}
    </>
  );
}
